/**
 * DEV-2521: server-derived maintenance capabilities.
 *
 * Maintenance classification and cancellation must come from the server fields
 * `is_owner_created_maintenance` and `can_cancel`. They are never inferred from `type`,
 * guest flags or property ownership, and missing fields never grant an action.
 */
import { isMaintenanceBlockType } from './bookingKinds';

export interface MaintenanceCapabilityFields {
  type?: string | null;
  is_owner_created_maintenance?: unknown;
  can_cancel?: unknown;
  timezone?: unknown;
}

/** Strict boolean read: only a literal `true` counts. Strings, numbers and undefined do not. */
export function readStrictBoolean(value: unknown): boolean | null {
  if (value === true) return true;
  if (value === false) return false;
  return null;
}

/** True only when the server explicitly reported `is_owner_created_maintenance: true`. */
export function isOwnerCreatedMaintenance(record: MaintenanceCapabilityFields | null | undefined): boolean {
  return !!record && readStrictBoolean(record.is_owner_created_maintenance) === true;
}

/** True only when the server explicitly reported `can_cancel: true` for this caller. */
export function canCancelFromServer(record: MaintenanceCapabilityFields | null | undefined): boolean {
  return !!record && readStrictBoolean(record.can_cancel) === true;
}

/**
 * Show "Cancel maintenance block" only for a maintenance block type with a server-granted
 * `can_cancel`. A legacy response without the field never shows the action.
 */
export function showCancelMaintenanceBlock(record: MaintenanceCapabilityFields | null | undefined): boolean {
  return !!record && isMaintenanceBlockType(record.type) && canCancelFromServer(record);
}

/**
 * Owner-created maintenance blocks that this caller cannot cancel (generated hybrid children,
 * started blocks, another owner's blocks). The UI directs the owner to the parent record
 * instead of exposing an independent cancel action.
 */
export function isNonCancellableOwnerMaintenance(
  record: MaintenanceCapabilityFields | null | undefined
): boolean {
  return (
    !!record &&
    isMaintenanceBlockType(record.type) &&
    isOwnerCreatedMaintenance(record) &&
    !canCancelFromServer(record)
  );
}

/** Property IANA zone from the record when present and non-empty. */
export function readReservationTimezone(
  record: MaintenanceCapabilityFields | null | undefined
): string | null {
  const zone = record?.timezone;
  return typeof zone === 'string' && zone.trim() !== '' ? zone : null;
}
